/**
 * Bridges Parts AI schema features to the reusable faceted-filter system.
 *
 * A PartSchema's effective features (canonical name + data type + unit) are
 * mapped to `FilterFieldConfig` entries the shared filter components already
 * understand:
 *   - string  -> multiSelect (options + counts from POST /parts/facets)
 *   - integer -> range
 *   - float   -> range
 *   - boolean -> boolean
 *
 * The dashboard renders these with `FacetedFilterSidebar` exactly as the
 * dataset viewer does, and `appliedFiltersToSearchBody` converts the user's
 * selection back into the structured `POST /parts/search` body.
 */

import { FilterFieldConfig, FilterOption } from "src/config/filterConfig";
import { AppliedFilter } from "src/utils/filterUrlUtils";
import type { PartSchemaFeature } from "../services/PartSchemaService";
import type {
  NumericFeaturePredicate,
  PartFacetsResponse,
  PartsSearchParams,
} from "../services/PartService";

/** Numeric feature types that render as a range slider. */
const NUMERIC_TYPES = new Set(["integer", "float"]);

/**
 * Build the filter sidebar config from a schema's effective features.
 *
 * Features are ordered by their `position`; the label prefers `display_name`
 * and appends the unit (e.g. "Size (in)") so range bounds read naturally.
 */
export function buildPartsFilterConfig(
  features: PartSchemaFeature[] | undefined,
): FilterFieldConfig[] {
  if (!features || features.length === 0) return [];

  return [...features]
    .sort((a, b) => a.position - b.position)
    .map((feature, index) => {
      const baseLabel = feature.display_name || feature.canonical_name;
      const label = feature.unit ? `${baseLabel} (${feature.unit})` : baseLabel;

      const base: FilterFieldConfig = {
        // The canonical name is the JSONB key the backend filters on.
        fieldName: feature.canonical_name,
        label,
        type: "text",
        order: feature.position ?? index,
        defaultExpanded: index < 4,
        showCounts: true,
      };

      if (feature.data_type === "boolean") {
        return { ...base, type: "boolean" };
      }

      if (NUMERIC_TYPES.has(feature.data_type)) {
        return {
          ...base,
          type: "range",
          rangeConfig: feature.unit
            ? { unit: feature.unit, unitPosition: "after" }
            : undefined,
        };
      }

      // string (and any unknown type) -> multi-select facet
      return {
        ...base,
        type: "multiSelect",
        searchable: true,
        maxVisibleOptions: 8,
      };
    });
}

/**
 * Convert the backend facets response into the `{ fieldName -> options }` map
 * the filter sidebar expects. Only string/boolean features produce discrete
 * options; numeric ranges are surfaced separately via `numericRangeConfigs`.
 */
export function partFacetsToFilterOptions(
  facets: PartFacetsResponse | undefined,
): Record<string, FilterOption[]> {
  const result: Record<string, FilterOption[]> = {};
  if (!facets?.facets) return result;

  for (const [fieldName, values] of Object.entries(facets.facets)) {
    result[fieldName] = values.map((v) => ({
      value: v.value,
      label: v.value,
      count: v.count,
    }));
  }
  return result;
}

/**
 * Seed each range filter's min/max bounds from the facets' numeric ranges so
 * the slider spans the actual data. Returns a new config array (does not
 * mutate the input).
 */
export function applyNumericRangeBounds(
  config: FilterFieldConfig[],
  facets: PartFacetsResponse | undefined,
): FilterFieldConfig[] {
  const ranges = facets?.numeric_ranges;
  if (!ranges) return [...config];

  return config.map((c) => {
    if (c.type !== "range") return c;
    const range = ranges[c.fieldName];
    if (!range) return c;
    return {
      ...c,
      rangeConfig: { ...c.rangeConfig, min: range.min, max: range.max },
    };
  });
}

/**
 * Translate the user's applied filters into the structured parts-search body.
 *
 * Uses the filter config to know each field's type:
 *   - multiSelect / boolean -> `string_feature_predicates[field] = [values]`
 *     (OR within a key; AND across keys — matched by the backend)
 *   - range -> `numeric_feature_predicates` (`ge` -> gte, `le` -> lte)
 *
 * `appliedFilters` from `useFilterUrlSync` represent multi-selects as `eq`
 * filters (value: string | string[]) and ranges as `ge`/`le` filters.
 */
export function appliedFiltersToSearchBody(
  appliedFilters: AppliedFilter[],
  config: FilterFieldConfig[],
): Pick<
  PartsSearchParams,
  "string_feature_predicates" | "numeric_feature_predicates"
> {
  const typeByField = new Map(config.map((c) => [c.fieldName, c.type]));
  const featureFilters: Record<string, string[]> = {};
  const numericPredicates: NumericFeaturePredicate[] = [];

  for (const filter of appliedFilters) {
    const fieldType = typeByField.get(filter.fieldName);
    if (!fieldType) continue; // not a schema feature (e.g. stray URL param)

    if (fieldType === "range") {
      if (filter.operator === "ge" || filter.operator === "le") {
        const value = Number(filter.value);
        if (!Number.isNaN(value)) {
          numericPredicates.push({
            key: filter.fieldName,
            op: filter.operator === "ge" ? "gte" : "lte",
            value,
          });
        }
      }
      continue;
    }

    // multiSelect / boolean: equality filter(s)
    if (filter.operator === "eq") {
      const values = Array.isArray(filter.value)
        ? filter.value
        : [filter.value];
      const existing = featureFilters[filter.fieldName] ?? [];
      featureFilters[filter.fieldName] = [...existing, ...values];
    }
  }

  return {
    string_feature_predicates:
      Object.keys(featureFilters).length > 0 ? featureFilters : undefined,
    numeric_feature_predicates:
      numericPredicates.length > 0 ? numericPredicates : undefined,
  };
}
